import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, AlertTriangle, Info, RefreshCw, Link2 } from 'lucide-react';
import { expenseAPI, paymentProofAPI } from '../../services/api';
import { useToast, useConfirm } from '../UIProvider';
import useDialog from '../../hooks/useDialog';
import DatePicker from '../DatePicker';
import Select from '../Select';
import SearchableSelect from '../SearchableSelect';
import PaymentProofUpload from '../PaymentProofUpload';
import { PAYMENT_MODES, withRetiredMode, paymentNeedsProof } from '../../constants/payments';
import { groupDigits, handleAmountInput } from '../../utils/money';
import AttachmentPicker from './AttachmentPicker';
import DescriptionSuggest from './DescriptionSuggest';
import {
  DepartmentPills, formatCurrency, formatDate, todayISO, errorText, inputClass, labelClass,
} from './shared';

// The one form for an expense. Built for a run of entries typed from a stack of bills or a
// WhatsApp thread: the department, the date and the way it was paid stay put between saves, the
// description offers what was entered before under that department, and Save & add another keeps
// the form open with the cursor back on the description.
//
// What the server does with it (ExpenseService): a non-cash expense whose cheque has not been
// drawn yet gets its withdrawal recorded alongside, so the cheque is tracked and cash in hand is
// untouched. An entry identical to one already in the ledger comes back as POSSIBLE_DUPLICATE and
// is saved only once "Save anyway" is pressed.

const EMPTY = {
  entryDate: '',
  department: '',
  categoryId: '',
  description: '',
  amount: '',
  paymentMode: 'CASH',
  chequeNumber: '',
  chequeBank: '',
  txnNumber: '',
  notes: '',
  recordWithdrawal: true,
};

const NON_CASH_HINT = {
  CHEQUE: 'Paid by cheque: nothing left the cash box.',
  BANK_TRANSFER: 'Paid from the bank account: nothing left the cash box.',
  MOBILE_WALLET: 'Paid from a wallet: nothing left the cash box.',
};

const ExpenseEntryModal = ({ editing, cats, initialDepartment, meta, onClose, onSaved }) => {
  const showToast = useToast();
  const confirm = useConfirm();
  const isEditing = !!editing;

  const [form, setForm] = useState(() => {
    if (editing) {
      const isChild = editing.categoryId && editing.categoryId !== editing.rootCategoryId;
      return {
        ...EMPTY,
        entryDate: editing.entryDate || todayISO(),
        department: editing.rootCategoryId || '',
        categoryId: isChild ? editing.categoryId : '',
        description: editing.description || '',
        amount: String(Math.round(Number(editing.amount || 0)) || ''),
        paymentMode: editing.paymentMode || 'CASH',
        chequeNumber: editing.chequeNumber || '',
        chequeBank: editing.chequeBank || '',
        txnNumber: editing.txnNumber || '',
        notes: editing.notes || '',
        recordWithdrawal: true,
      };
    }
    const department = initialDepartment && initialDepartment !== 'ALL' ? initialDepartment : '';
    return { ...EMPTY, entryDate: todayISO(), department };
  });
  const [errors, setErrors] = useState({});
  const [proofFiles, setProofFiles] = useState([]);
  const [proofError, setProofError] = useState(null);
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState(editing?.attachments || []);
  const [suggestions, setSuggestions] = useState([]);
  const [chequeInfo, setChequeInfo] = useState(null);
  const [duplicate, setDuplicate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const descriptionRef = useRef(null);
  // The form saves and stays open, so the hook would keep remembering the typing of a saved entry.
  const dirtyRef = useRef(false);
  const dialogRef = useDialog(onClose, { isDirty: () => dirtyRef.current });

  const set = useCallback((key, value) => {
    dirtyRef.current = true;
    setForm(f => ({ ...f, [key]: value }));
    setErrors(e => (e[key] ? { ...e, [key]: undefined } : e));
    setDuplicate(null);
  }, []);

  const roots = cats.roots;
  const children = useMemo(() => cats.childrenOf(form.department), [cats, form.department]);
  const nonCash = form.paymentMode !== 'CASH';
  const isCheque = form.paymentMode === 'CHEQUE';
  const needsProof = paymentNeedsProof(form.paymentMode);
  const banks = meta?.banks || [];

  // The first department when there is only one, so a one-department clinic never has to pick.
  useEffect(() => {
    if (!form.department && roots.length === 1) setForm(f => ({ ...f, department: roots[0].id }));
  }, [roots, form.department]);

  // What was entered before under this department, for the description field.
  useEffect(() => {
    let alive = true;
    if (!form.department) { setSuggestions([]); return undefined; }
    expenseAPI.getSuggestions(form.department)
      .then(list => { if (alive) setSuggestions(Array.isArray(list) ? list : []); })
      .catch(() => { if (alive) setSuggestions([]); });
    return () => { alive = false; };
  }, [form.department]);

  // The bank a department last drew from, offered as soon as a cheque is chosen.
  useEffect(() => {
    if (!isCheque || form.chequeBank || !form.department) return;
    const last = meta?.bankByDepartment?.[form.department];
    if (last) setForm(f => (f.chequeBank ? f : { ...f, chequeBank: last }));
  }, [isCheque, form.chequeBank, form.department, meta]);

  // What is already known about the cheque number typed, after a short pause.
  useEffect(() => {
    const number = form.chequeNumber.trim();
    if (!isCheque || !number) { setChequeInfo(null); return undefined; }
    let alive = true;
    const t = setTimeout(() => {
      expenseAPI.lookupCheque(number, form.chequeBank || undefined)
        .then(info => { if (alive) setChequeInfo(info); })
        .catch(() => { if (alive) setChequeInfo(null); });
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [isCheque, form.chequeNumber, form.chequeBank]);

  const validate = () => {
    const e = {};
    if (!form.department) e.department = 'Pick the department.';
    if (!form.entryDate) e.entryDate = 'Pick the date.';
    if (!form.description.trim()) e.description = 'Describe the expense.';
    if (!form.amount || Number(form.amount) <= 0) e.amount = 'Enter the amount.';
    if (isCheque && !form.chequeNumber.trim()) e.chequeNumber = 'Enter the cheque number.';
    if (needsProof && proofFiles.length === 0 && !(editing?.paymentProofs || []).length) {
      setProofError('Attach the payment screenshot for this method.');
      e.proof = true;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const payload = (confirmDuplicate) => ({
    entryDate: form.entryDate,
    description: form.description.trim(),
    amount: Number(form.amount),
    entryType: 'EXPENSE',
    categoryId: form.categoryId || form.department,
    paymentMode: form.paymentMode,
    chequeNumber: isCheque ? form.chequeNumber.trim() : '',
    chequeBank: nonCash ? form.chequeBank : '',
    txnNumber: nonCash && !isCheque ? form.txnNumber.trim() : '',
    notes: form.notes.trim(),
    confirmDuplicate,
    recordWithdrawal: nonCash ? form.recordWithdrawal : false,
  });

  const save = async (addAnother, confirmDuplicate = false) => {
    if (!validate()) return;
    setSaving(true);
    try {
      const files = { paymentProofs: proofFiles, attachments: attachmentFiles };
      const saved = isEditing
        ? await expenseAPI.updateEntry(editing.id, payload(confirmDuplicate), files)
        : await expenseAPI.createEntry(payload(confirmDuplicate), files);
      showToast('success', `${isEditing ? 'Updated' : 'Saved'}: ${saved.description}, ${formatCurrency(saved.amount)}`);
      if (addAnother) {
        setSavedCount(n => n + 1);
        // The department, the date and the way it was paid stay; the rest is the next entry's.
        setForm(f => ({ ...f, description: '', amount: '', txnNumber: '', notes: '', recordWithdrawal: true }));
        setProofFiles([]);
        setAttachmentFiles([]);
        setDuplicate(null);
        setErrors({});
        dirtyRef.current = false;
        onSaved(saved, { keepOpen: true });
        descriptionRef.current?.focus();
      } else {
        dirtyRef.current = false;
        onSaved(saved, { keepOpen: false });
      }
    } catch (err) {
      if (err.status === 409 && err.code === 'POSSIBLE_DUPLICATE') {
        setDuplicate(err.body?.duplicate || {});
      } else {
        showToast('error', errorText(err, 'Failed to save'));
      }
    } finally {
      setSaving(false);
    }
  };

  const removeExisting = async (a) => {
    const ok = await confirm({
      title: 'Remove this file?',
      message: `${a.originalFileName || 'The file'} will be removed from the entry.`,
      confirmText: 'Remove',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await expenseAPI.deleteAttachment(a.id);
      setExistingAttachments(list => list.filter(x => x.id !== a.id));
    } catch (err) {
      showToast('error', errorText(err, 'Could not remove the file'));
    }
  };

  const pickSuggestion = (s) => {
    setForm(f => ({
      ...f,
      description: s.description,
      amount: f.amount ? f.amount : String(Math.round(Number(s.amount || 0)) || ''),
    }));
    setErrors(e => ({ ...e, description: undefined }));
    dirtyRef.current = true;
  };

  const modeOptions = withRetiredMode(PAYMENT_MODES, form.paymentMode);

  const renderChequeHint = () => {
    if (!isCheque || !form.chequeNumber.trim()) return null;
    if (chequeInfo?.found) {
      const remaining = Number(chequeInfo.remaining || 0);
      return (
        <div className="rounded-xl bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-800 flex items-start gap-2">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            Cheque {chequeInfo.chequeNumber}{chequeInfo.bank ? ` (${chequeInfo.bank})` : ''} is known:
            withdrawn {formatCurrency(chequeInfo.withdrawn)} on {formatDate(chequeInfo.firstDate)},
            {' '}{formatCurrency(chequeInfo.spent)} spent out of it so far,
            {' '}{formatCurrency(remaining)} left. This expense is paid out of that cash.
          </span>
        </div>
      );
    }
    return null;
  };

  const renderWithdrawalChoice = () => {
    if (!nonCash) return null;
    if (isEditing && editing.linkedWithdrawal) {
      return (
        <div className="rounded-xl bg-gray-50 border border-gray-200 px-3 py-2 text-xs text-gray-700 flex items-start gap-2">
          <Link2 className="w-4 h-4 flex-shrink-0 mt-0.5 text-gray-400" />
          <span>
            A withdrawal of {formatCurrency(editing.linkedWithdrawal.amount)} is recorded with this expense
            and follows its changes. Switching to cash removes it.
          </span>
        </div>
      );
    }
    if (isCheque && chequeInfo?.found) return null;
    return (
      <label className="flex items-start gap-2.5 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 cursor-pointer">
        <input
          type="checkbox"
          checked={form.recordWithdrawal}
          onChange={(e) => set('recordWithdrawal', e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-amber-300 text-emerald-600 focus:ring-emerald-500"
        />
        <span>
          <span className="font-semibold">Also record the withdrawal from the bank.</span>{' '}
          {NON_CASH_HINT[form.paymentMode] || 'Nothing left the cash box.'} Recording the withdrawal with it keeps
          cash in hand right and{isCheque ? ' puts the cheque on the Cheques page' : ' shows the payment on the ledger'}.
          Untick it if the withdrawal is already entered or will be entered on its own.
        </span>
      </label>
    );
  };

  return (
    <div ref={dialogRef} className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="expense-entry-title">
        <div className="flex-shrink-0 px-6 py-4 bg-gradient-to-r from-navy-800 to-navy-700 flex items-center justify-between">
          <div>
            <h2 id="expense-entry-title" className="text-lg font-semibold text-white">{isEditing ? 'Edit expense' : 'Add expense'}</h2>
            {savedCount > 0 && <p className="text-xs text-navy-100 mt-0.5">{savedCount} saved so far in this run</p>}
          </div>
          <button aria-label="Close" onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
            <X className="h-5 w-5 text-white" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Department first: everything else hangs off it. */}
          <div>
            <label className={labelClass}>Department <span className="text-red-500">*</span></label>
            <DepartmentPills roots={roots} value={form.department} onChange={(v) => { set('department', v); set('categoryId', ''); }} required label="Department" />
            {errors.department && <p className="text-red-500 text-xs mt-1">{errors.department}</p>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Category</label>
              <Select
                value={form.categoryId}
                onChange={(v) => set('categoryId', v)}
                placeholder={form.department ? 'Whole department' : 'Pick the department first'}
                disabled={!form.department}
                options={[
                  { value: '', label: 'Whole department' },
                  ...children.map(c => ({ value: c.id, label: c.name })),
                ]}
                buttonClassName="px-4 py-2.5 rounded-xl"
              />
            </div>
            <div>
              <label className={labelClass}>Date <span className="text-red-500">*</span></label>
              <DatePicker value={form.entryDate} onChange={(v) => set('entryDate', v)} max={todayISO()} error={!!errors.entryDate} />
              {errors.entryDate && <p className="text-red-500 text-xs mt-1">{errors.entryDate}</p>}
            </div>
          </div>

          <div>
            <label htmlFor="expense-description" className={labelClass}>What was it for <span className="text-red-500">*</span></label>
            <DescriptionSuggest
              id="expense-description"
              inputRef={descriptionRef}
              value={form.description}
              onChange={(v) => set('description', v)}
              suggestions={suggestions}
              onPick={pickSuggestion}
              placeholder="e.g. New Tariq, Dr Maaz salary, Electricity bill"
              error={errors.description}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="expense-amount" className={labelClass}>Amount <span className="text-red-500">*</span></label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">Rs</span>
                <input
                  id="expense-amount"
                  type="text"
                  inputMode="numeric"
                  value={groupDigits(form.amount)}
                  onChange={(e) => handleAmountInput(e, form.amount, (d) => set('amount', d))}
                  placeholder="0"
                  className={`${inputClass} pl-11 text-lg font-semibold ${errors.amount ? 'border-red-300' : ''}`}
                />
              </div>
              {errors.amount && <p className="text-red-500 text-xs mt-1">{errors.amount}</p>}
            </div>
            <div>
              <label className={labelClass}>Paid by</label>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Paid by">
                {modeOptions.map(o => (
                  <button
                    key={o.value}
                    type="button"
                    aria-pressed={form.paymentMode === o.value}
                    onClick={() => set('paymentMode', o.value)}
                    className={`px-3.5 py-2 rounded-xl text-sm font-medium border-2 transition-colors ${
                      form.paymentMode === o.value
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {isCheque && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="expense-cheque" className={labelClass}>Cheque number <span className="text-red-500">*</span></label>
                <input
                  id="expense-cheque"
                  type="text"
                  value={form.chequeNumber}
                  onChange={(e) => set('chequeNumber', e.target.value)}
                  placeholder="e.g. 110641"
                  className={`${inputClass} font-mono ${errors.chequeNumber ? 'border-red-300' : ''}`}
                />
                {errors.chequeNumber && <p className="text-red-500 text-xs mt-1">{errors.chequeNumber}</p>}
              </div>
              <div>
                <SearchableSelect label="Bank" value={form.chequeBank} onChange={(v) => set('chequeBank', v)} options={banks} placeholder="Bank" allowCustom />
              </div>
            </div>
          )}

          {nonCash && !isCheque && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="expense-txn" className={labelClass}>Transfer reference</label>
                <input
                  id="expense-txn"
                  type="text"
                  value={form.txnNumber}
                  onChange={(e) => set('txnNumber', e.target.value)}
                  placeholder="Optional, e.g. TRX-88213445"
                  className={`${inputClass} font-mono`}
                />
              </div>
              <div>
                <SearchableSelect label="Bank" value={form.chequeBank} onChange={(v) => set('chequeBank', v)} options={banks} placeholder="Bank" allowCustom />
              </div>
            </div>
          )}

          {renderChequeHint()}
          {renderWithdrawalChoice()}

          {needsProof && (
            <PaymentProofUpload
              paymentMode={form.paymentMode}
              existing={(editing?.paymentProofs || []).map(p => ({ ...p, url: paymentProofAPI.fileUrl(p.id) }))}
              files={proofFiles}
              onFiles={(f) => { setProofFiles(f); dirtyRef.current = true; }}
              error={proofError}
              onClearError={() => setProofError(null)}
            />
          )}

          <div>
            <label htmlFor="expense-notes" className={labelClass}>Notes</label>
            <textarea
              id="expense-notes"
              rows={2}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              placeholder="Anything worth remembering: the vendor, what the bill covered"
              className={`${inputClass} resize-none`}
            />
          </div>

          <AttachmentPicker
            files={attachmentFiles}
            onFiles={(f) => { setAttachmentFiles(f); dirtyRef.current = true; }}
            existing={existingAttachments}
            onRemoveExisting={isEditing ? removeExisting : undefined}
            fileUrl={expenseAPI.attachmentFileUrl}
          />

          {duplicate && (
            <div role="alert" className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900">
                  <p className="font-semibold">This looks like an entry already in the ledger.</p>
                  <p className="mt-0.5">
                    "{duplicate.description}" for {formatCurrency(duplicate.amount)} on {formatDate(duplicate.entryDate)}
                    {duplicate.categoryPath ? ` under ${duplicate.categoryPath}` : ''}
                    {duplicate.createdByName ? `, entered by ${duplicate.createdByName}` : ''}.
                  </p>
                  <div className="mt-2 flex gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => save(false, true)}
                      disabled={saving}
                      className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 disabled:opacity-50"
                    >
                      Save anyway, it is a separate expense
                    </button>
                    <button
                      type="button"
                      onClick={() => setDuplicate(null)}
                      className="px-3 py-1.5 rounded-lg bg-white border border-amber-300 text-amber-900 text-xs font-semibold hover:bg-amber-100"
                    >
                      Let me check it
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-3 flex-wrap">
          <button type="button" onClick={onClose} className="px-5 py-2.5 text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors font-medium">
            Cancel
          </button>
          {!isEditing && (
            <button
              type="button"
              onClick={() => save(true)}
              disabled={saving}
              className="px-5 py-2.5 text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors font-medium disabled:opacity-50"
            >
              Save &amp; add another
            </button>
          )}
          <button
            type="button"
            onClick={() => save(false)}
            disabled={saving}
            className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <RefreshCw className="h-4 w-4 animate-spin" />}
            {isEditing ? 'Save changes' : 'Save expense'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExpenseEntryModal;
